// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormContext, FormDatePicker, FormSelect, FormSubmitButton, MasterDetailForm, Row } from '@digital/ui';

//Api
import { confirmarSolicitacaoChancelaGJMinuta, ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs } from '@app/api/sas/sasProposta';

//Files
import { editandoState, modalAcoesIdState, reloadVendorListState, vendorListDocsState } from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import { EStatusGenerico } from '@app/enums/EStatusGenerico';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalConfirmarSolicitacaoChancelaGJMinuta = ({ sasProposta }: Props) => {

    const [reloadVendorList, setReloadVendorList] = useRecoilState(reloadVendorListState);
    const [modalAcoesId, setModalAcoesId] = useRecoilState(modalAcoesIdState);
    const [editando, setEditando] = useRecoilState(editandoState);
    const [exibirModal, setExibirModal] = useState(false);

    const vendorListDocs = useRecoilValue(vendorListDocsState);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    useEffect(() => {
        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        confirmarSolicitacaoChancelaGJMinuta(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    setReloadVendorList(!reloadVendorList);
                                    handleCancelAndClose();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            });
                    });

                },
                onCancel: () => { setModalAcoesId(''); }
            });
        } else {
            confirmarSolicitacaoChancelaGJMinuta(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        setReloadVendorList(!reloadVendorList);
                        handleCancelAndClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Confirmar Chancela">
                <Button icon='commons-ok' onClick={() => { setExibirModal(true); }} />
            </Tooltip>

            <Visibility visible={exibirModal}>

                <Modal title={`Confirmar Chancela`} width='780px' visible={exibirModal} onCancel={handleCancel} footer={null} >
                    <Visibility visible={isObjNotEmpty(sasProposta) && exibirModal} >
                        <MasterDetailForm
                            key={`form_ModalDispensarSolicitacaoChancelaGJMinuta_${sasProposta.id}`}
                            actions={[
                                <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                                <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                            ]}
                            initialValues={sasProposta}
                            validationSchema={
                                Yup.object().shape({
                                    arquivo: isObjNotEmpty(doc) && doc.anexoConfirmarSolicitacaoChancelaGJMinuta ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                                })
                            }
                        >
                            <FormContext.Consumer>
                                {({ formProps }) => (
                                    <>
                                        <Row>
                                            <FormSelect
                                                label='Status'
                                                name='statusConfirmarSolicitacaoChancelaGJMinuta'
                                                value={sasProposta.statusConfirmarSolicitacaoChancelaGJMinuta
                                                    ? sasProposta.statusConfirmarSolicitacaoChancelaGJMinuta
                                                    : EStatusGenerico.PENDENTE
                                                }
                                                options={[
                                                    { text: EStatusGenerico.PENDENTE, value: EStatusGenerico.PENDENTE },
                                                    { text: EStatusGenerico.ENVIADO, value: EStatusGenerico.ENVIADO },
                                                ]}
                                                span={6}
                                                disabled
                                            />

                                            <FormDatePicker
                                                format='DD/MM/YYYY'
                                                name='dataStatusConfirmarSolicitacaoChancelaGJMinuta'
                                                label='Data de envio da solicitação da chancela'
                                                required
                                                span={6} />
                                        </Row>
                                        <br></br>
                                        <Row>
                                            <UploadFile
                                                idDoComponente='arquivo-ModalDispensarSolicitacaoChancelaGJMinuta'
                                                type='primary'
                                                hasFile={false}
                                                hasLabel={true}
                                                icon='ui-upload'
                                                span={6}
                                                onGetArquivo={() => {

                                                    if (isObjNotEmpty(doc.anexoConfirmarSolicitacaoChancelaGJMinuta)) {
                                                        return Promise.resolve({
                                                            id: doc.anexoConfirmarSolicitacaoChancelaGJMinuta.id,
                                                            nome: doc.anexoConfirmarSolicitacaoChancelaGJMinuta.nome,
                                                            status: "",
                                                            statusArquivoId: doc.anexoConfirmarSolicitacaoChancelaGJMinuta.statusArquivoId || "",
                                                        } as IArquivo);
                                                    }
                                                    return Promise.resolve(undefined);

                                                }}
                                                showDownload={true}
                                            />
                                        </Row>
                                    </>
                                )}
                            </FormContext.Consumer>

                        </MasterDetailForm>
                    </Visibility>
                </Modal>

            </Visibility>
        </>
    );
};